import { useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { LogOut, ChevronDown, Menu, X } from 'lucide-react';

import { useAuth } from '../auth/AuthProvider.jsx';
import { env } from '../lib/env.js';
import { GlobalSearch } from '../components/GlobalSearch.jsx';
import { ADMIN_NAV } from './adminNav.js';

function flattenItems(nav) {
  const out = [];
  for (const group of nav) {
    if (group.section) {
      for (const it of group.items || []) out.push(it);
    } else {
      out.push(group);
    }
  }
  return out;
}

function findActive(items, pathname) {
  // Pick the longest match so /admin/ai/personality wins over /admin/ai.
  let match = null;
  for (const it of items) {
    if (it.to && (pathname === it.to || pathname.startsWith(`${it.to}/`))) {
      if (!match || it.to.length > match.to.length) match = it;
    }
  }
  return match;
}

function NavItem({ item, hasPermission }) {
  if (item.perm && !hasPermission(item.perm)) return null;
  const Icon = item.icon;
  return (
    <li>
      <NavLink
        to={item.to}
        className={({ isActive }) =>
          clsx(
            'group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors',
            isActive
              ? 'bg-sidebar-active text-sidebar-ink font-medium'
              : 'text-sidebar-ink-muted hover:bg-sidebar-elevated hover:text-sidebar-ink'
          )
        }
      >
        {Icon && (
          <Icon
            className="h-4 w-4 shrink-0 text-sidebar-ink-muted group-hover:text-sidebar-ink"
            strokeWidth={1.75}
          />
        )}
        <span>{item.label}</span>
      </NavLink>
    </li>
  );
}

function UserMenu({ user, role, onLogout }) {
  const [open, setOpen] = useState(false);
  const initials = (user && user.email ? user.email[0] : '?').toUpperCase();
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left hover:bg-sidebar-elevated"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink text-[11px] font-semibold text-white">
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-sidebar-ink">{user && user.email}</p>
          <p className="text-[11px] text-sidebar-ink-muted">{role}</p>
        </div>
        <ChevronDown className={clsx('h-3.5 w-3.5 text-sidebar-ink-muted transition-transform', open && 'rotate-180')} strokeWidth={1.75} />
      </button>
      {open && (
        <div className="animate-zoom-in absolute bottom-full left-0 right-0 mb-1 origin-bottom rounded-md border border-border bg-surface-elevated p-1 shadow-elevated">
          <NavLink
            to="/admin/account"
            onClick={() => setOpen(false)}
            className="block rounded px-2 py-1.5 text-xs text-ink hover:bg-surface-subtle"
          >
            Account e sicurezza
          </NavLink>
          <button
            type="button"
            onClick={onLogout}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-ink hover:bg-surface-subtle"
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} />
            Esci
          </button>
        </div>
      )}
    </div>
  );
}

export function AdminLayout() {
  const { user, role, hasPermission, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const flat = useMemo(() => flattenItems(ADMIN_NAV), []);
  const active = useMemo(() => findActive(flat, location.pathname), [flat, location.pathname]);

  // Two postures over the same admin: the calm "Operativo" landing (the
  // Operations Center, no sidebar) and the full "Gestione" back-office. The
  // sidebar is only shown in Gestione.
  const operativo = location.pathname === '/admin';

  // A tap on any link inside the drawer dismisses it on mobile. Handled via
  // click delegation on the nav/footer so navigation and close stay in sync
  // without a state-syncing effect.
  const closeMobileNav = () => setMobileNavOpen(false);

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-surface">
      {/* Backdrop: only rendered on mobile when the drawer is open. */}
      {mobileNavOpen && (
        <button
          type="button"
          aria-label="Chiudi menu"
          onClick={() => setMobileNavOpen(false)}
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
        />
      )}

      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-ink transition-transform duration-200 ease-out lg:static lg:z-auto lg:translate-x-0',
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center gap-2 px-4 py-4">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-white border border-sidebar-border text-white">
            <img src="/favicon.png" alt={env.appName} className="h-4 w-4" />
          </span>
          <p className="text-sm font-semibold tracking-tight text-sidebar-ink">{env.appName}</p>
          <button
            type="button"
            aria-label="Chiudi menu"
            onClick={() => setMobileNavOpen(false)}
            className="ml-auto rounded-md p-1 text-sidebar-ink-muted hover:bg-sidebar-elevated hover:text-sidebar-ink lg:hidden"
          >
            <X className="h-5 w-5" strokeWidth={1.75} />
          </button>
        </div>
        <div className="border-t border-sidebar-border" />
        <nav className="flex-1 overflow-y-auto px-2 py-3" onClick={closeMobileNav}>
          {ADMIN_NAV.map((group, idx) => {
            const visible = (group.items || []).filter((it) => !it.perm || hasPermission(it.perm));
            if (visible.length === 0) return null;
            return (
              <div key={idx} className="mb-4">
                <p className="px-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-sidebar-ink-muted">
                  {group.section}
                </p>
                <ul className="flex flex-col gap-0.5">
                  {visible.map((it) => (
                    <NavItem key={it.to} item={it} hasPermission={hasPermission} />
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
        <div className="border-t border-sidebar-border p-2">
          <UserMenu user={user} role={role} onLogout={onLogout} />
        </div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-12 items-center justify-between gap-2 border-b border-border bg-surface-elevated px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3 text-xs text-ink-muted">
            <button
              type="button"
              aria-label="Apri menu"
              onClick={() => setMobileNavOpen(true)}
              className="-ml-1 rounded-md p-1 text-ink-muted hover:bg-surface-subtle hover:text-ink lg:hidden"
            >
              <Menu className="h-5 w-5" strokeWidth={1.75} />
            </button>
            {/* Posture switch: calm Operations Center vs full back-office. */}
            <div className="flex items-center rounded-lg border border-border bg-surface p-0.5">
              <button
                type="button"
                onClick={() => navigate('/admin')}
                className={clsx('rounded-md px-3 py-1 text-xs font-medium transition-colors', operativo ? 'bg-brand text-white' : 'text-ink-muted hover:text-ink')}
              >
                Operativo
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/dashboard')}
                className={clsx('rounded-md px-3 py-1 text-xs font-medium transition-colors', !operativo ? 'bg-brand text-white' : 'text-ink-muted hover:text-ink')}
              >
                Gestione
              </button>
            </div>
            {!operativo && active && (
              <span className="hidden truncate text-ink sm:inline">{active.label}</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-ink-muted">
            {hasPermission('search:global') && <GlobalSearch basePath="/admin" />}
            <span className="hidden md:inline">{user && user.email}</span>
          </div>
        </header>
        <div id="admin-scroll-area" className="flex-1 overflow-y-auto">
          <div key={location.pathname} className="animate-fade-in mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
